import { useSelector } from 'react-redux';

import { OrgRoleType } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import { RoleType } from '@features/team-management/constants';
import { getLocalStorageItem } from '@utils/storage';

import { useGetLeadOrgConfigQuery } from '../queries/LeadManagementQuery';

const BULK_ASSIGN_ROLES: string[] = [
  OrgRoleType.OWNER,
  OrgRoleType.ORGANISATION_ADMIN,
  RoleType.SALES_MANAGER
];

/**
 * Org LMS rule: when `is_lead_assignment_allowed_for_agent` is false, sales agents
 * must not change assignee or use bulk assignee actions. Lead creation is limited
 * to manual add only (CSV import and Meta sync are hidden).
 * Org LMS rule: when `is_lead_deletion_allowed_for_sales_agent` is false, sales agents
 * must not delete leads.
 * Config is fetched only for the sales-agent role.
 */
const useLeadAgentRestrictions = () => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const role = rolesAndPermissions?.role;
  const isSalesAgent = role === RoleType.SALES_AGENT;

  const { data: orgConfig, isLoading } = useGetLeadOrgConfigQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId || !isSalesAgent }
  );

  const isAssigneeSelectionDisabled =
    isSalesAgent && orgConfig?.is_lead_assignment_allowed_for_agent === false;

  const isLeadDeletionDisabled =
    isSalesAgent &&
    orgConfig?.is_lead_deletion_allowed_for_sales_agent === false;

  const isAgentReassignAllowed =
    isSalesAgent && orgConfig?.is_lead_assignment_allowed_for_agent === true;

  const isBulkAssignEnabled =
    BULK_ASSIGN_ROLES.includes(role) || isAgentReassignAllowed;

  return {
    orgConfig,
    isLoading,
    isSalesAgent,
    isAssigneeSelectionDisabled,
    isLeadDeletionDisabled,
    isBulkAssignEnabled
  };
};

export default useLeadAgentRestrictions;
